import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { MessageContext, ReactState } from '../../Common/Contexts'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import { createDevice, getDevice, updateDevice } from '../../../NonReact/common/API/EntityAPIClient'
import { isStringNotEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import Tabs from '../../Common/Components/Tabs'
import { IconName } from '../../Common/Components/Icon'
import { Tab } from '../../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { DEFAULT_DEVICE_TYPE, DeviceConfigurationTabID } from '../Constants/Constants'
import type { DeviceModalCapabilityFlags } from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS
} from '../Spec/DeviceModalCapabilityKeys'
import {
    AgentMemberRow,
    DeviceAdminRequest,
    DeviceConfigurationForm,
    DeviceDetailDTO,
    DeviceMemberRow
} from '../Types/Types'
import { mapDeviceDtoToForm, mapDeviceFormToRequest } from '../Utils/mappers'
import { mapAgentMemberRows, mapDeviceMemberRows } from '../Utils/memberRows'
import { resolveDeviceModalCapabilityFlags } from '../Utils/resolveDeviceModalCapabilityFlags'
import { DeviceGeneralTab } from '../Tabs/DeviceGeneralTab'
import { DeviceAdvancedTab } from '../Tabs/DeviceAdvancedTab'
import { DeviceMembersTab } from '../Tabs/DeviceMembersTab'
import { AgentMembersTab } from '../Tabs/AgentMembersTab'
import { DeviceDnisTab } from '../Tabs/DeviceDnisTab'
import { LinkedEntitiesTab } from '../Tabs/LinkedEntitiesTab'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { APIData, JSONResponse } from '../../../NonReact/common/Types/Types'
import { DeviceResponse } from '../../../NonReact/Types/Types'

type Props = {
    closeModal: () => void
    refreshAction: () => void
    filterTelSysID: number
    filterPartitionID: number
    partitionDisplayLabel?: string
    telSysType?: TelSysTypes
    selectedDeviceNumber?: string
    viewOnly?: boolean
    /**
     * Passed to member tabs for {@link RangeCombo} and other controls that mirror router admin styling.
     */
    isRouterMode?: boolean
    linkedEntitiesRefreshNonce?: number
    initialFormValues?: DeviceConfigurationForm
    initialDeviceMembers?: string[]
    initialDnisNumbers?: string[]
    onSelectLinkedGroupDevice?: (groupDeviceNumber: string) => void
    onSelectLinkedUser?: (userId: string) => void
    onLoadedDeviceDetail?: (deviceDetail: Nullable<DeviceDetailDTO>) => void
}

/**
 * Device modal tabs and submit workflow.
 *
 * @param props - Tabs props.
 * @returns Tabs content with form state and actions.
 */
export const DeviceTabs: FC<Props> = ({
    closeModal,
    refreshAction,
    filterTelSysID,
    filterPartitionID,
    partitionDisplayLabel,
    telSysType,
    selectedDeviceNumber,
    viewOnly,
    isRouterMode,
    linkedEntitiesRefreshNonce,
    initialFormValues,
    initialDeviceMembers,
    initialDnisNumbers,
    onSelectLinkedGroupDevice,
    onSelectLinkedUser,
    onLoadedDeviceDetail
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const { displayError }: MessageContextType = useContext(MessageContext)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const isEditMode: boolean = isStringNotEmpty(selectedDeviceNumber)

    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(DeviceConfigurationTabID.GENERAL)
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(isEditMode)
    const [serverError, setServerError]: ReactState<string> = useState<string>(EMPTY_STRING)
    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [deviceMemberRows, setDeviceMemberRows]: ReactState<DeviceMemberRow[]> = useState<DeviceMemberRow[]>(
        mapDeviceMemberRows(undefined, initialDeviceMembers)
    )
    const [agentMemberRows, setAgentMemberRows]: ReactState<AgentMemberRow[]> = useState<AgentMemberRow[]>([])
    const [dnisMemberRows, setDnisMemberRows]: ReactState<DeviceMemberRow[]> = useState<DeviceMemberRow[]>(
        mapDeviceMemberRows(undefined, initialDnisNumbers)
    )
    const [deviceTypeDisplayLabel, setDeviceTypeDisplayLabel]: ReactState<string> = useState<string>(EMPTY_STRING)
    /**
     * Authoritative backend {@code DeviceType} from {@code getDevice} in change/view mode. {@code watch} can lag
     * behind {@code reset} on the first paint after loading because tab fields mount only when the form is shown.
     */
    const [loadedEditDeviceType, setLoadedEditDeviceType]: ReactState<string> = useState<string>(EMPTY_STRING)
    const [initialDeviceMemberRows] = useState<DeviceMemberRow[]>(mapDeviceMemberRows(undefined, initialDeviceMembers))
    const resolvedInitialFormValues: DeviceConfigurationForm =
        initialFormValues ?? mapDeviceDtoToForm(null, { partitionDisplayLabel, partitionIdInTelSys: filterPartitionID })

    const methods: UseFormMethods<DeviceConfigurationForm> = useForm<DeviceConfigurationForm>({
        defaultValues: resolvedInitialFormValues,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })
    const { handleSubmit, reset, watch }: UseFormMethods<DeviceConfigurationForm> = methods

    const watchedDeviceType: string = watch('general.deviceType', DEFAULT_DEVICE_TYPE)
    const resolvedDeviceType: string = useMemo((): string => {
        if (isEditMode && isStringNotEmpty(loadedEditDeviceType)) {
            return loadedEditDeviceType
        }

        return watchedDeviceType
    }, [isEditMode, loadedEditDeviceType, watchedDeviceType])

    const capabilityFlags: DeviceModalCapabilityFlags = useMemo(
        (): DeviceModalCapabilityFlags => resolveDeviceModalCapabilityFlags(resolvedDeviceType, telSysType),
        [resolvedDeviceType, telSysType]
    )

    const showDeviceMembersTab: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP]
    const showAgentMembersTab: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP]
    const showDnisMembersTab: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS]
    const showLinkedEntitiesTab: boolean = isEditMode

    useEffect((): void => {
        if (activeTabID === DeviceConfigurationTabID.DEVICE_MEMBERS && !showDeviceMembersTab) {
            setActiveTabID(DeviceConfigurationTabID.GENERAL)
        }

        if (activeTabID === DeviceConfigurationTabID.AGENT_MEMBERS && !showAgentMembersTab) {
            setActiveTabID(DeviceConfigurationTabID.GENERAL)
        }

        if (activeTabID === DeviceConfigurationTabID.DNIS_MEMBERS && !showDnisMembersTab) {
            setActiveTabID(DeviceConfigurationTabID.GENERAL)
        }

        if (activeTabID === DeviceConfigurationTabID.LINKED_ENTITIES && !showLinkedEntitiesTab) {
            setActiveTabID(DeviceConfigurationTabID.GENERAL)
        }
    }, [activeTabID, showAgentMembersTab, showDeviceMembersTab, showDnisMembersTab, showLinkedEntitiesTab])

    useEffect((): void => {
        if (!isEditMode || IsVarEmpty(selectedDeviceNumber)) {
            setLoadedEditDeviceType(EMPTY_STRING)
            setDeviceMemberRows(initialDeviceMemberRows)
            onLoadedDeviceDetail?.(null)

            return
        }

        setLoadedEditDeviceType(EMPTY_STRING)
        setIsLoading(true)

        getDevice(filterTelSysID, filterPartitionID, selectedDeviceNumber)
            .then((response: JSONResponse<APIData<DeviceResponse>>): void => {
                if (!isMounted.current) return

                const dto: DeviceDetailDTO = (response?.Data as DeviceDetailDTO) ?? null

                if (dto) {
                    setLoadedEditDeviceType(dto.DeviceType ?? EMPTY_STRING)
                    reset(mapDeviceDtoToForm(dto, { partitionDisplayLabel, partitionIdInTelSys: filterPartitionID }))
                    setDeviceTypeDisplayLabel(dto.DeviceType ?? EMPTY_STRING)
                    onLoadedDeviceDetail?.(dto)
                    setDnisMemberRows(mapDeviceMemberRows(dto.DNISNumberDetails, dto.DNISNumbers))
                    setDeviceMemberRows(mapDeviceMemberRows(dto.DeviceMemberDetails, dto.DeviceMembers))
                    setAgentMemberRows(mapAgentMemberRows(dto.ACDAgentMemberDetails, dto.ACDAgentMembers))
                }

                setIsLoading(false)
            })
            .catch((error: Error): void => {
                if (isMounted.current) setIsLoading(false)

                onLoadedDeviceDetail?.(null)
                displayError(error)
            })
    }, [
        displayError,
        filterPartitionID,
        filterTelSysID,
        initialDeviceMemberRows,
        isEditMode,
        isMounted,
        onLoadedDeviceDetail,
        partitionDisplayLabel,
        reset,
        selectedDeviceNumber
    ])

    const resolveAddModePartitionID = (partIDInTelSys: string): number => {
        const parsedPartitionID: number = Number.parseInt(partIDInTelSys, 10)

        return Number.isNaN(parsedPartitionID) ? filterPartitionID : parsedPartitionID
    }

    const submitForm = async (form: DeviceConfigurationForm): Promise<void> => {
        setIsSubmitting(true)
        setServerError(EMPTY_STRING)

        const memberDeviceNumbers: string[] = deviceMemberRows.map((row: DeviceMemberRow): string => row.DeviceNumber)
        const memberAgentNumbers: string[] = agentMemberRows.map((row: AgentMemberRow): string => row.AgentNumber)
        const dnisNumbers: string[] = dnisMemberRows.map((row: DeviceMemberRow): string => row.DeviceNumber)
        const request: DeviceAdminRequest = mapDeviceFormToRequest(form, memberDeviceNumbers, memberAgentNumbers, {
            telSysType,
            isAddMode: !isEditMode,
            dnisNumbers
        })
        const addModePartitionID: number = resolveAddModePartitionID(form.general.partIDInTelSys)

        const onSuccess = (): void => {
            closeModal()
            refreshAction()
        }

        const onError = (error: Error): void => {
            if (isMounted.current) {
                setServerError(error.message ?? language.get(DeviceModal.DeviceModal_RequestFailed))
                setIsSubmitting(false)
            }
        }

        if (isEditMode && isStringNotEmpty(selectedDeviceNumber)) {
            updateDevice(filterTelSysID, filterPartitionID, selectedDeviceNumber, request)
                .then(onSuccess)
                .catch(onError)

            return
        }

        createDevice(filterTelSysID, addModePartitionID, request).then(onSuccess).catch(onError)
    }

    const tabs: Tab[] = [
        {
            ID: DeviceConfigurationTabID.GENERAL,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(DeviceModal.DeviceModal_Tab_General),
                iconName: IconName.SETTINGS,
                onClick: (): void => setActiveTabID(DeviceConfigurationTabID.GENERAL)
            },
            content: (
                <DeviceGeneralTab
                    filterTelSysID={filterTelSysID}
                    isEditMode={isEditMode}
                    viewOnly={viewOnly}
                    deviceTypeDisplayLabel={deviceTypeDisplayLabel}
                    telSysType={telSysType}
                    capabilityFlags={capabilityFlags}
                />
            ),
            hasError: false
        },
        {
            ID: DeviceConfigurationTabID.ADVANCED,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(DeviceModal.DeviceModal_Tab_Advanced),
                iconName: IconName.SETTINGS,
                onClick: (): void => setActiveTabID(DeviceConfigurationTabID.ADVANCED)
            },
            content: (
                <DeviceAdvancedTab viewOnly={viewOnly} capabilityFlags={capabilityFlags} telSysType={telSysType} />
            ),
            hasError: false
        },
        {
            ID: DeviceConfigurationTabID.DEVICE_MEMBERS,
            isDisplayed: showDeviceMembersTab,
            tabsMenuItem: {
                title: language.get(DeviceModal.DeviceModal_Tab_DeviceMembers),
                iconName: IconName.DEVICES_SIDERBAR,
                onClick: (): void => setActiveTabID(DeviceConfigurationTabID.DEVICE_MEMBERS)
            },
            content: (
                <DeviceMembersTab
                    viewOnly={viewOnly}
                    filterTelSysID={filterTelSysID}
                    filterPartitionID={filterPartitionID}
                    openModalDeviceNumber={selectedDeviceNumber}
                    selectedRows={deviceMemberRows}
                    onRowsChange={setDeviceMemberRows}
                    isRouterMode={isRouterMode}
                />
            ),
            hasError: false
        },
        {
            ID: DeviceConfigurationTabID.AGENT_MEMBERS,
            isDisplayed: showAgentMembersTab,
            tabsMenuItem: {
                title: language.get(DeviceModal.DeviceModal_Tab_AgentMembers),
                iconName: IconName.AGENTS_SIDEBAR,
                onClick: (): void => setActiveTabID(DeviceConfigurationTabID.AGENT_MEMBERS)
            },
            content: (
                <AgentMembersTab
                    viewOnly={viewOnly}
                    filterTelSysID={filterTelSysID}
                    filterPartitionID={filterPartitionID}
                    selectedRows={agentMemberRows}
                    onRowsChange={setAgentMemberRows}
                    isRouterMode={isRouterMode}
                />
            ),
            hasError: false
        },
        {
            ID: DeviceConfigurationTabID.DNIS_MEMBERS,
            isDisplayed: showDnisMembersTab,
            tabsMenuItem: {
                title: language.get(DeviceModal.DeviceModal_Tab_DnisMembers),
                iconName: IconName.TAG,
                onClick: (): void => setActiveTabID(DeviceConfigurationTabID.DNIS_MEMBERS)
            },
            content: (
                <DeviceDnisTab
                    viewOnly={viewOnly}
                    filterTelSysID={filterTelSysID}
                    filterPartitionID={filterPartitionID}
                    selectedRows={dnisMemberRows}
                    onRowsChange={setDnisMemberRows}
                    isRouterMode={isRouterMode}
                />
            ),
            hasError: false
        },
        {
            ID: DeviceConfigurationTabID.LINKED_ENTITIES,
            isDisplayed: showLinkedEntitiesTab,
            tabsMenuItem: {
                title: language.get(DeviceModal.DeviceModal_Tab_LinkedEntities),
                iconName: IconName.REPORT,
                onClick: (): void => setActiveTabID(DeviceConfigurationTabID.LINKED_ENTITIES)
            },
            content: (
                <>
                    {showLinkedEntitiesTab && activeTabID === DeviceConfigurationTabID.LINKED_ENTITIES ? (
                        <LinkedEntitiesTab
                            mode='device'
                            filterTelSysID={filterTelSysID}
                            filterPartitionID={filterPartitionID}
                            entityNumber={selectedDeviceNumber ?? EMPTY_STRING}
                            refreshNonce={linkedEntitiesRefreshNonce}
                            onSelectGroupDevice={onSelectLinkedGroupDevice}
                            onSelectLinkedUser={onSelectLinkedUser}
                        />
                    ) : null}
                </>
            ),
            hasError: false
        }
    ]

    if (isLoading) return <div className='device-config-loading'>{language.get(DeviceModal.DeviceModal_Loading)}</div>

    return (
        <div className='device-configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitForm)}
                >
                    <Tabs tabs={tabs} activeTabID={activeTabID} />
                    {viewOnly ? (
                        <div className='device-config-footer modal-footer'>
                            <RoundedButton type='button' onClick={closeModal} shrink>
                                {language.get(DeviceModal.DeviceModal_Button_Close)}
                            </RoundedButton>
                        </div>
                    ) : (
                        <div className='device-config-footer modal-footer'>
                            {isStringNotEmpty(serverError) ? <div className='server-error'>{serverError}</div> : null}
                            <RoundedButton type='button' onClick={closeModal} disabled={isSubmitting} shrink>
                                {language.get(DeviceModal.DeviceModal_Button_Cancel)}
                            </RoundedButton>
                            <RoundedButton type='submit' disabled={isSubmitting} shrink>
                                {language.get(DeviceModal.DeviceModal_Button_Save)}
                            </RoundedButton>
                        </div>
                    )}
                </form>
            </FormProvider>
        </div>
    )
}
